// src/components/chat/ChatMessage.jsx - ИСПРАВЛЕННАЯ ВЕРСИЯ
import React, { useState } from 'react';
import { Badge, Button, Spinner } from 'react-bootstrap';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';

const ChatMessage = ({ message, isOwn, onReply, roomId }) => {
  const [showActions, setShowActions] = useState(false);
  const { retrySendMessage } = useChat();
  const { user } = useAuth();

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Сегодня';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Вчера';
    } else {
      return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2);
  };

  // ✅ Функция для получения имени пользователя
  const getUserName = () => {
    // 1. Если это наше сообщение и user есть в контексте
    if (isOwn && user?.name) {
      return user.name;
    }
    
    // 2. Если в сообщении есть user_name
    if (message.user_name) {
      return message.user_name;
    }
    
    // 3. Если есть author (из обогащенных данных)
    if (message.author?.name) {
      return message.author.name;
    }
    
    // 4. Пытаемся получить из telegram данных
    if (message.author?.telegram_first_name || message.author?.telegram_last_name) {
      const firstName = message.author.telegram_first_name || '';
      const lastName = message.author.telegram_last_name || '';
      if (firstName && lastName) return `${firstName} ${lastName}`;
      if (firstName) return firstName;
      if (lastName) return lastName;
    }
    
    // 5. Fallback
    return 'Пользователь';
  };

  const getStatusIcon = () => {
    if (message.status === 'sending') {
      return <Spinner animation="border" size="sm" className="ms-1" />;
    } else if (message.status === 'failed') {
      return <i className="bi bi-exclamation-triangle text-danger ms-1"></i>;
    } else if (message.status === 'queued') {
      return <i className="bi bi-clock-history text-warning ms-1"></i>;
    }
    return null;
  };

  const handleRetry = () => {
    if (message.status === 'failed' && message.id?.startsWith('temp-')) {
      retrySendMessage(message.id, roomId, message.content);
    }
  };

  const handleReplyClick = () => {
    if (onReply && !message.is_system) {
      onReply({
        id: message.id,
        user_name: getUserName(),
        content: message.content
      });
    }
  };

  if (message.is_system) {
    return (
      <div className="text-center my-3">
        <Badge bg="secondary" className="py-1 px-3" style={{ fontSize: '0.8rem' }}>
          <i className="bi bi-gear me-1"></i>
          {message.content}
        </Badge>
      </div>
    );
  }

  // Проверка на удаленное сообщение
  if (message.is_deleted) {
    return (
      <div className={`d-flex ${isOwn ? 'justify-content-end' : 'justify-content-start'} mb-2 opacity-50`}>
        <div className="d-flex gap-2" style={{ maxWidth: '70%' }}>
          <div className="flex-shrink-0">
            <div className="bg-secondary rounded-circle d-flex align-items-center justify-content-center text-white"
                 style={{ width: '36px', height: '36px', fontSize: '0.9rem' }}>
              <i className="bi bi-trash"></i>
            </div>
          </div>
          <div>
            <div className="rounded-3 p-2 bg-light">
              <small className="fw-semibold d-block text-muted">{getUserName()}</small>
              <p className="mb-0 fst-italic text-muted">Сообщение удалено</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`d-flex ${isOwn ? 'justify-content-end' : 'justify-content-start'} mb-2`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className={`d-flex gap-2 ${isOwn ? 'flex-row-reverse' : ''}`} style={{ maxWidth: '70%' }}>
        {/* Аватар */}
        <div className="flex-shrink-0 position-relative">
          <div 
            className={`rounded-circle d-flex align-items-center justify-content-center text-white ${
              message.status === 'failed' ? 'bg-danger' : 'bg-primary'
            }`}
            style={{ width: '36px', height: '36px', fontSize: '0.9rem' }}
          >
            {getInitials(getUserName())}
          </div>
          {message.status === 'failed' && (
            <div className="position-absolute top-0 start-100 translate-middle">
              <i className="bi bi-exclamation-circle-fill text-danger"></i>
            </div>
          )}
        </div>

        {/* Контент сообщения */}
        <div>
          {/* Ответ на сообщение */}
          {message.reply_to && (
            <div className="mb-1 p-2 bg-light rounded border-start border-primary" style={{ fontSize: '0.8rem' }}>
              <small className="text-muted d-block">
                <i className="bi bi-arrow-return-right me-1"></i>
                В ответ на сообщение от {message.reply_to.user_name || 'пользователя'}:
              </small>
              <div className="text-truncate" style={{ maxWidth: '200px' }}>
                {message.reply_to.content}
              </div>
            </div>
          )}

          {/* Основное сообщение */}
          <div 
            className={`rounded-3 p-2 ${
              message.status === 'failed' ? 'bg-danger bg-opacity-10 border border-danger' :
              isOwn ? 'bg-primary text-white' : 'bg-light'
            }`}
            style={{ wordBreak: 'break-word' }}
          >
            {/* ✅ ИСПРАВЛЕНО: используем getUserName() */}
            <small className={`fw-semibold d-block ${isOwn ? 'text-white-50' : 'text-muted'}`}>
              {getUserName()}
              {message.status === 'edited' && (
                <span className="ms-2 small opacity-75">(ред.)</span>
              )}
            </small>
            
            <p className="mb-0 d-flex align-items-center" style={{ whiteSpace: 'pre-wrap' }}>
              {message.content}
              {getStatusIcon()}
            </p>

            {/* Вложения */}
            {message.attachments && message.attachments.length > 0 && (
              <div className="mt-2 d-flex flex-wrap gap-1">
                {message.attachments.map((att, idx) => (
                  <div key={idx} className="attachment-preview">
                    {att.type?.startsWith('image/') ? (
                      <img 
                        src={att.url} 
                        alt="attachment" 
                        style={{ maxWidth: '100px', maxHeight: '100px', borderRadius: '4px' }}
                      />
                    ) : (
                      <a href={att.url} target="_blank" rel="noopener noreferrer">
                        <i className="bi bi-file-earmark me-1"></i>
                        {att.name}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Мета-информация */}
          <div className="d-flex justify-content-between align-items-center mt-1">
            <small className="text-muted d-flex align-items-center">
              {formatTime(message.created_at)}
              {message.edited_at && (
                <span className="ms-1 small text-muted">(изменено)</span>
              )}
            </small>

            {/* Кнопки действий */}
            {showActions && !isOwn && !message.is_system && (
              <div className="d-flex gap-1">
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-muted"
                  onClick={handleReplyClick}
                  title="Ответить"
                >
                  <i className="bi bi-reply"></i>
                </Button>
              </div>
            )}

            {/* Кнопка повторной отправки для failed сообщений */}
            {message.status === 'failed' && (
              <Button
                variant="link"
                size="sm"
                className="p-0 text-danger"
                onClick={handleRetry}
                title="Повторить отправку"
              >
                <i className="bi bi-arrow-clockwise"></i>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;